import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { apiFailureReason, emberApi, type HomeResponse, type HomeTail, type TreeNode, type TreeYouResponse } from '../api';
import { track, trackFailed } from '../../../../../shared-web/track';
import { useSession } from '../../auth';
import { useTalkSheet } from '../talk-sheet';
import { currentTrack } from '../tracks';
import { BanyanScene, SCENE, type SceneLetter } from './BanyanScene';
import { EmberTapCard } from './EmberTapCard';
import { KnockLetter } from '../components/KnockLetter';
import { useKnocks } from '../components/use-knocks';
import { isAwaited, isLetter, letterId, letterLanded, letterOpened, markLetterLanded } from '../components/knock-letters';
import { Link } from '../components/Link';
import { TreeSearch } from './TreeSearch';
import { LabelFlame } from './LabelFlame';
import { HeroComposer } from './HeroComposer';
import { ChatsSign } from './ChatsSign';
import { MeetPeopleIntro, type IntroFrom } from '../pages/friends/MeetPeopleIntro';
import { chatSkinAt, chatsUnread } from '../chats';
import { refreshChats, useChatsOnce } from '../chats-store';
import './TreeHome.css';
import './home-skin.css';

/** The home opened the Meet people intro by itself in this page load: a read of the chats made before it still says due. */
let documentIntroOpened = false;

/** The tag demonstration (§4.17): first look after the tree settles, how long a tag hangs, the beat of dark canopy before the next light. */
const PEEK_FIRST_MS = 1000; // 2026-09-06 拍板: the first look one second after arrival
const PEEK_SHOW_MS = 5000; // 2026-09-07 三次拍板: long enough to read the name, the count and the doors under it
const PEEK_PAUSE_MS = 2000; // 2026-09-07 二次拍板: the string of lights with no gap in it read as too fast to follow — a tag fades out, the canopy rests a beat, the next fades in
const PEEK_PRESS_MS = 700; // 2026-09-08: from the second look on, the tree presses the light before the tag hangs — the ripple's length

/**
 * Whether this browser has ever opened a light by hand (2026-09-08 拍板). The
 * demonstration's tag carries a first row, `Tap any light.`, until it has:
 * prod showed the tag alone read as a slideshow of topics — of 371 tree-era
 * arrivals 4% touched a light (paid traffic 1%), and the more looks a person
 * watched the more often they took the ground's door, never a light. Once a
 * light has been tapped the words have done their work; the demonstration
 * itself goes on. Per browser, kept across visits; not product state.
 */
const TAPPED_KEY = 'ember.tree.tapped';
function lightTapped(): boolean {
  try { return window.localStorage.getItem(TAPPED_KEY) === '1'; } catch { return false; }
}
function markLightTapped(): void {
  try { window.localStorage.setItem(TAPPED_KEY, '1'); } catch { /* the hint stays; nothing else changes */ }
}

/**
 * S01's first screen (v4.0 §4.17): the Banyan by day or by night, the viewer sitting
 * under it, the lights in its canopy, and the one door. Touch a light and it
 * brightens where it hangs, its name and doors beside it; drag to see more of
 * the canopy; the search at the top right lists the lights the words could
 * mean, and choosing one walks the tree to it.
 *
 * The lights ride the home payload (public, server-rendered); who is sitting
 * under the tree is fetched after mount — the animal is per browser and never
 * part of a shared document.
 */
export function TreeHome({ home, failed, nav, initialLightId = null }: {
  home: HomeResponse | null;
  failed: boolean;
  /** The landing nav, rendered over the scene with the search docked in it and, for a visitor who has told, the door back to their Ember. */
  /** `hasApp`: null until /api/tree/you answered; then whether this person already has the app (the horn's badge waits for it). */
  nav: (search: ReactNode, yourEmber: TreeYouResponse['your_ember'], hasApp: boolean | null) => ReactNode;
  /** `/?light=<id>`: open this light's paper on arrival (from a place page). */
  initialLightId?: string | null;
}): ReactElement {
  const openTalk = useTalkSheet();
  const hostRef = useRef<HTMLDivElement>(null);
  // The window (§4.17): today's tree rides the home payload for the first
  // paint; the viewer's own window replaces it once the browser knows who is
  // sitting there. A tap does not move it (2026-09-06 走查): the tree used to
  // re-grow around whatever was touched, and a light flying to the centre with
  // every other light shuffling behind it read as the tree jumping, not as a
  // light being lit. The window is only re-centred on a light that is not in
  // it, or that a narrow screen may be showing none of — the search, or a
  // place page's /?light=<id>.
  const [nodes, setNodes] = useState<TreeNode[]>(() => home?.tree?.nodes ?? []);
  const [animal, setAnimal] = useState<string | null>(null);
  const [yourEmber, setYourEmber] = useState<TreeYouResponse['your_ember']>(null);
  const [hasApp, setHasApp] = useState<boolean | null>(null);
  // This browser's or account's conversations (2026-09-16 拍板; the unread ones in turn since 2026-10-20): the
  // sentence under the headline becomes the bar over the field. `thread`: there is one to show.
  const [tail, setTail] = useState<HomeTail | null>(null);
  const thread = Boolean(tail?.rows.length);
  // The open light: its name, count and two doors hang beside it in the
  // canopy, like a tag on a lantern. No paper over the tree; the ground keeps
  // its one door.
  const [open, setOpen] = useState<TreeNode | null>(null);
  // The open tag was hung by the address, not by a hand (2026-09-22): a
  // `/?light=<id>` arrival — a place page's way back, and since today an ad's
  // landing — opens the light exactly as a tap would, so the tag's exposure is
  // recorded under its own state (tree_light_open state=arrival) and "tags
  // opened" stays what a person did. Cleared by the next hand on the tree.
  const [linked, setLinked] = useState(false);
  const requestSeq = useRef(0);

  // Ember's letters (§4.9, 2026-09-06 拍板): every letter waiting on this
  // person lies at the figure's feet as an envelope, and a tap opens the
  // paper. Whether a letter FLIES in is decided once per
  // letter per visit, the first time this tree sees it — and remembered at
  // once, so a re-render mid-flight never restarts the drift, and the next
  // look at the tree finds the letter already on the ground.
  const { status: sessionStatus } = useSession();
  const { knocks, update: updateKnock } = useKnocks(sessionStatus !== 'loading');
  // The sign to Chats on the ground (2026-10-21 founder): everyone signed in; lit while something is unread.
  const { data: chats } = useChatsOnce(sessionStatus === 'authenticated');
  // Meet people's intro (2026-10-27 founder): the ? on the sign opens it, and the home by itself once a person. It
  // folds back into the sign (2026-10-28): `folded` once the one the home opened by itself has (the sign's "new!" and ?
  // wait for it), `landing` while the sign glows and they come out.
  const [intro, setIntro] = useState<IntroFrom | null>(null);
  const [folded, setFolded] = useState(false);
  const [landing, setLanding] = useState(false);
  useEffect(() => {
    if (!landing) return undefined;
    const t = window.setTimeout(() => setLanding(false), 2500);
    return () => window.clearTimeout(t);
  }, [landing]);
  const flight = useRef(new Map<string, boolean>());
  const [, letterTick] = useState(0);
  // A text's letter link (§4.14, `/?letter=<matchId>`): the letter it names
  // flies in even if it landed on an earlier visit, and once it has landed the
  // sheet opens on it — the page shows the letter arriving and being opened,
  // the way it would have been by hand.
  const linkedLetter = useRef<string>(typeof window === 'undefined' ? '' : (new URLSearchParams(window.location.search).get('letter') || ''));
  const [initialExpanded, setInitialExpanded] = useState('');
  const sceneLetters: SceneLetter[] = knocks.filter(isLetter).map((k) => {
    const id = letterId(k);
    let fly = flight.current.get(id);
    if (fly === undefined) {
      fly = !letterLanded(id) || (Boolean(linkedLetter.current) && k.match_id === linkedLetter.current);
      markLetterLanded(id);
      flight.current.set(id, fly);
    }
    return { id, sealed: !letterOpened(id), fly, waiting: isAwaited(k), invite: k.letter === 'room_invite' };
  });
  const [lettersOpen, setLettersOpen] = useState(false);
  useEffect(() => {
    const wanted = linkedLetter.current;
    if (!wanted) return undefined;
    const linked = knocks.find((k) => isLetter(k) && k.match_id === wanted);
    if (!linked) return undefined;
    linkedLetter.current = '';
    const id = letterId(linked);
    // Let the flight land first (BanyanScene.css banyan-letter-fall, 1500ms).
    const timer = window.setTimeout(() => {
      endPeek();
      setInitialExpanded(id);
      setLettersOpen(true);
    }, 1800);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knocks]);

  // A first look (2026-09-06 走查): nothing on the screen said the lights could
  // be touched. Once the tree has settled, one light shows its tag — the same
  // tag a tap hangs, doors and all — for a few seconds, fades it out, and after
  // a beat another light does the same, and so on while nobody touches the
  // tree: a demonstration, not a state. 2026-09-07 二次拍板: the handover with
  // no beat between the tags (one fading out under the next) ran the canopy as
  // one unbroken string and was too fast to read; each tag now fades in, hangs,
  // and fades out on its own. It never runs over a light the person (or a place
  // page) already opened, and any touch ends it for the visit.
  const [peeking, setPeeking] = useState(false);
  // Read after mount: the server render has no browser to ask, and the row is
  // only ever on a demonstration's tag, which never hangs before the tree settles.
  const [tapped, setTapped] = useState(true);
  useEffect(() => { setTapped(lightTapped()); }, []);
  // The press (2026-09-08): prod showed the tag alone teaches slowly — of the
  // people who watched four or more looks before touching the tree, the first
  // touch came at a median 112 s; 159 arrivals watched two to seven looks and
  // scrolled away instead. From the second look on the tree first PRESSES the
  // light (BanyanScene pressId: a dip and a touch ripple), then hangs the tag —
  // the demonstration performs the act, not only its result. Never on the
  // first look: the people who tap within seconds of the first tag need no
  // nudge. `round` counts the looks this visit; the pressed node is kept in a
  // ref so a window re-grow mid-press can tell whether it is still there.
  const [press, setPress] = useState<string | null>(null);
  const peek = useRef<{ timer: number | undefined; phase: 'idle' | 'showing' | 'done'; last: string | null; round: number; node: string | null }>({ timer: undefined, phase: 'idle', last: null, round: 0, node: null });
  const openRef = useRef<TreeNode | null>(null);
  openRef.current = open;
  const nodesRef = useRef<TreeNode[]>(nodes);
  nodesRef.current = nodes;
  /** After `delay`, hang a tag on one light for a while, then fade it out and arm the next look. */
  const armPeek = (delay: number): void => {
    const p = peek.current;
    window.clearTimeout(p.timer);
    p.timer = window.setTimeout(() => {
      // Somebody opened a light of their own between the arming and the tick.
      if (p.phase !== 'idle' || openRef.current) { p.phase = 'done'; return; }
      // Nobody is looking (2026-09-09): a tab parked behind another app kept
      // the loop running at the browser's background cadence, and 90% of the
      // first day's tree_tap_demo rows (836 of 931) came from eight such
      // tabs. The visibility listener below re-arms it when the page is back.
      if (document.visibilityState === 'hidden') return;
      const all = nodesRef.current;
      const mine = all.filter((n) => n.mine);
      // A track's tree (/?t=) is its own lights filled out with near neighbours
      // (2026-10-07): picking from the whole window hung a neighbour's tag —
      // a marriage or cheating light — over most track landings, the first
      // thing an ad's arrival read. The demonstration points at the track's own.
      const onTrack = all.filter((n) => n.track);
      const pick = mine.length ? mine : onTrack.length ? onTrack : all;
      const pool = pick.length > 1 ? pick.filter((n) => n.id !== p.last) : pick;
      // Nothing to point at: look again when the window next changes.
      if (pool.length === 0) return;
      const node = pool[Math.floor(Math.random() * pool.length)];
      p.phase = 'showing';
      p.last = node.id;
      p.node = node.id;
      p.round += 1;
      const hang = (): void => {
        setPress(null);
        setPeeking(true);
        setOpen(node);
        p.timer = window.setTimeout(() => {
          setPeeking(false); setOpen(null);
          p.node = null;
          p.phase = 'idle';
          armPeek(PEEK_PAUSE_MS);
        }, PEEK_SHOW_MS);
      };
      if (p.round >= 2) {
        // Imperative, one row per press (the seen hook would fold a visit's
        // presses into one per light): with tree_tag_demo and tree_light_open
        // this is the demonstration's own funnel — pressed, tagged, touched.
        track('ui.seen', { name: 'tree_tap_demo', node_id: node.id });
        setPress(node.id);
        p.timer = window.setTimeout(hang, PEEK_PRESS_MS);
      } else {
        hang();
      }
    }, delay);
  };
  /** A touch: the demonstration is over for the visit. */
  const endPeek = (): void => {
    window.clearTimeout(peek.current.timer);
    if (peek.current.phase === 'showing') { setPress(null); setPeeking(false); setOpen(null); }
    peek.current.node = null;
    peek.current.phase = 'done';
  };

  useEffect(() => {
    if (nodes.length === 0 && home?.tree?.nodes.length) setNodes(home.tree.nodes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home]);

  /** A phone's narrower canopy holds fewer lights comfortably. */
  const windowSize = (): number => ((hostRef.current?.clientWidth || 390) < 560 ? 26 : 40);

  /** Re-centre the window; the latest request wins. */
  const recentre = async (focus: string | null): Promise<TreeNode[]> => {
    const seq = ++requestSeq.current;
    const view = await emberApi.treeView({ focus, size: windowSize(), track: currentTrack() });
    if (seq !== requestSeq.current) return [];
    setNodes(view.nodes);
    return view.nodes;
  };
  const [wide, setWide] = useState(false);
  // Scrolled out of view the scene holds still (2026-09-18): the fireflies,
  // the lights and Ember kept moving under How it works and the FAQ, and each
  // of those frames was a full repaint of the page for a tree nobody could
  // see. A class on the box pauses the CSS loops (TreeHome.css); Ember's
  // wander is held through emberActive. Back in view, everything resumes
  // where it stood.
  const [onScreen, setOnScreen] = useState(true);
  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting));
    io.observe(host);
    return () => io.disconnect();
  }, []);

  // The nav stays at the top of a phone's screen all the way down (2026-10-10
  // 拍板, TreeHome.css). It is drawn for the dusk — pale marks, glass circles,
  // the search field — so once the tree has scrolled out from behind it, it
  // carries a band of the tree's own night with it rather than being redrawn
  // for paper. Docked = the scene's bottom edge has passed the nav's.
  const navRef = useRef<HTMLDivElement>(null);
  const [navDocked, setNavDocked] = useState(false);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let frame = 0;
    const check = (): void => {
      frame = 0;
      setNavDocked(host.getBoundingClientRect().bottom <= (navRef.current?.offsetHeight ?? 0));
    };
    const onScroll = (): void => { if (!frame) frame = window.requestAnimationFrame(check); };
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    // A failed read leaves nobody under the tree and no way back to this
    // browser's Ember — absorbed, so recorded.
    emberApi.treeYou().then((r) => { if (alive) { setAnimal(r.animal); setYourEmber(r.your_ember ?? null); setTail(r.tail ?? null); setHasApp(Boolean(r.has_app)); } }).catch((err: unknown) => { if (alive) trackFailed('home_load', 'tree_you_failed', { error_detail: apiFailureReason(err) }); });
    // The viewer's own window (their lights at the centre) — or today's tree
    // again for someone who has never told, which is what is already painted.
    if (!initialLightId) {
      // The same lights in the same order as already painted (a first visit
      // on a phone: today's tree again) is not a new tree — a redraw here
      // re-laid out the canopy a second after it appeared (2026-09-25).
      emberApi.treeView({ size: windowSize(), track: currentTrack() }).then((v) => {
        if (!alive || ((v.mode === 'today' || v.mode === 'track') && windowSize() >= 40)) return;
        setNodes((cur) => (cur.length === v.nodes.length && cur.every((n, i) => n.id === v.nodes[i].id) ? cur : v.nodes));
      }).catch((err: unknown) => { if (alive) trackFailed('home_load', 'tree_view_failed', { error_detail: apiFailureReason(err) }); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const measure = (): void => {
      if (!hostRef.current) return;
      // A tablet or an unfolded phone (2026-10-09): the box is taller than it
      // is wide, but the scene row above the ground is not — and the phone's
      // 560-wide scene, sliced to that row's width, was blown up past a
      // phone's own scale (×1.07–1.33) with the crown cut off at the top.
      // Past a phone's width, a scene row wider than the phone scene's own
      // proportion takes the wide scene instead. Phones stay as they were.
      const host = hostRef.current;
      const scene = host.querySelector<HTMLElement>('.tree-home-scene');
      const w = host.clientWidth;
      const sceneH = Math.max(1, scene?.clientHeight || host.clientHeight);
      setWide(w / Math.max(1, host.clientHeight) > 0.9 || (w >= 560 && w / sceneH > SCENE.w / SCENE.h));
    };
    measure();
    window.addEventListener('resize', measure);
    // The ground grows when the door does (the thread's tail arriving), and
    // the scene row gives up the room without a window resize.
    const scene = hostRef.current?.querySelector('.tree-home-scene');
    const ro = scene && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (scene) ro?.observe(scene);
    return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, []);

  // A place page's "See it on the tree": centre the window on that light and open it.
  const openedInitial = useRef(false);
  useEffect(() => {
    if (openedInitial.current || !initialLightId) return;
    openedInitial.current = true;
    recentre(initialLightId).then((view) => {
      const node = view.find((n) => n.id === initialLightId);
      if (node) { setLinked(true); setOpen(node); }
    }).catch(() => { trackFailed('tree_walk', 'load_failed'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLightId]);

  useEffect(() => {
    const p = peek.current;
    if (p.phase === 'done' || initialLightId || nodes.length === 0) return;
    // The window re-grew under the demonstration (the viewer's own window
    // arriving a beat after the first paint): keep pointing at the light if it
    // is still there, put the tag away and wait for the next look if it left.
    if (p.phase === 'showing') {
      if (!nodes.some((n) => n.id === p.node)) {
        window.clearTimeout(p.timer);
        setPress(null); setPeeking(false); setOpen(null);
        p.node = null;
        p.phase = 'idle';
        armPeek(PEEK_PAUSE_MS);
      }
      return;
    }
    armPeek(PEEK_FIRST_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, initialLightId]);
  useEffect(() => () => window.clearTimeout(peek.current.timer), []);
  // The demonstration is for someone looking at the tree. Hidden, or the
  // composer's field in use (2026-10-15 拍板: a tag hanging itself and Ember
  // wandering behind the list of lights pulled the eye off the sentence):
  // put the tag away and stop the loop (a person who opened a light of their
  // own — phase done — is left alone). Visible again, the field let go: one
  // beat, then the next look.
  const [composing, setComposing] = useState(false);
  useEffect(() => {
    const hold = (): void => {
      const p = peek.current;
      window.clearTimeout(p.timer);
      if (p.phase === 'showing') { setPress(null); setPeeking(false); setOpen(null); }
      p.node = null;
      p.phase = 'idle';
    };
    const resume = (): void => {
      if (!openRef.current && nodesRef.current.length > 0) armPeek(PEEK_PAUSE_MS);
    };
    const isField = (t: EventTarget | null): boolean => t instanceof HTMLElement && t.classList.contains('hero-input');
    const onVisibility = (): void => {
      const p = peek.current;
      if (p.phase === 'done' || initialLightId) return;
      if (document.visibilityState === 'hidden') hold();
      else if (!isField(document.activeElement)) resume();
    };
    const onFocusIn = (e: FocusEvent): void => {
      if (!isField(e.target)) return;
      setComposing(true);
      if (peek.current.phase !== 'done' && !initialLightId) hold();
    };
    const onFocusOut = (e: FocusEvent): void => {
      if (!isField(e.target)) return;
      setComposing(false);
      if (peek.current.phase !== 'done' && !initialLightId && document.visibilityState !== 'hidden') resume();
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLightId]);

  // Ember's card (拍板 2026-09-14): up while the person has tapped Ember, at
  // the point above its head; any other tap on the scene, a light opening or
  // Escape puts it away. Ember itself pauses while anything is open on the scene.
  const [emberCard, setEmberCard] = useState<{ x: number; y: number } | null>(null);
  const close = (): void => { endPeek(); setLinked(false); setOpen(null); setEmberCard(null); };
  /** A tap in the canopy: the light brightens where it hangs and its tag opens beside it. Nothing moves. */
  const openHere = (node: TreeNode): void => { endPeek(); markLightTapped(); setTapped(true); setEmberCard(null); setLinked(false); setOpen(node); };
  /** Walk (the search, a place page): a named light may hang outside this
      window, or outside the strip a narrow screen shows of it, so this one
      journey still re-grows the tree around it — it is being gone to, not
      touched. */
  const walkTo = (node: TreeNode): void => {
    endPeek();
    setLinked(false);
    setOpen(node);
    recentre(node.id).then((view) => {
      setOpen(view.find((n) => n.id === node.id) || node);
    }).catch(() => { trackFailed('tree_walk', 'load_failed'); });
  };
  // Leaving a light: the sky, another light, or Escape — no × to find.
  useEffect(() => {
    if (!open && !emberCard) return undefined;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, emberCard]);
  const tell = (node: TreeNode): void => {
    close();
    openTalk({ entryType: 'node', entryId: node.id, returnTo: '/' });
  };

  // Meet people's intro opens by itself when the edge says it is due (friends.intro), on the tree — never over the
  // composer in use or the letters — and is marked shown the moment it opens, so no other device or visit opens it again.
  const introDue = chats?.friends?.intro === true;
  useEffect(() => {
    if (!introDue || documentIntroOpened || composing || lettersOpen) return;
    documentIntroOpened = true;
    setIntro('auto');
    emberApi.friendsIntroShown().catch((err: unknown) => trackFailed('friends_intro_shown', apiFailureReason(err)));
  }, [introDue, composing, lettersOpen]);
  // The sign's "new!" and ? come out of the intro: not before the home's own has been and folded back.
  const signQuiet = intro === 'auto' || (introDue && !folded);
  const introClosed = (): void => {
    if (intro === 'auto') {
      setFolded(true);
      // The list read before it was shown says nothing of "new!" yet (friends.fresh): read it again.
      void refreshChats();
    }
    setIntro(null);
    setLanding(true);
  };

  // The first screen keeps the hour (2026-10-26 founder): the chat's skins on the same clock — Sand by day, Night from
  // 20:00 to 06:00 on the person's clock (the tail's, once /api/tree/you has answered; this device's until then, as
  // index.html set it before the first paint), looked at again every minute. It lives on the document (data-skin), so
  // the server's markup and the browser's are one; a flip while the page is open cross-fades (home-skin.css).
  const zone = tail?.clock?.zone ?? '';
  useEffect(() => {
    const doc = document.documentElement;
    const check = (): void => {
      const skin = chatSkinAt(new Date(), zone);
      if (doc.dataset.skin === skin) return;
      const flip = (): void => { doc.dataset.skin = skin; };
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (!still && typeof document.startViewTransition === 'function') document.startViewTransition(flip);
      else flip();
    };
    check();
    const timer = window.setInterval(check, 60_000);
    return () => window.clearInterval(timer);
  }, [zone]);

  return (
    <div className={`tree-home${onScreen ? '' : ' is-offscreen'}`} ref={hostRef}>
      {/* The scene takes what the ground leaves it; the ground band below is
          sized by the door, so the words never climb onto the figure. */}
      <div className="tree-home-scene">
        <BanyanScene
          nodes={nodes}
          animal={animal}
          highlightId={open?.id ?? null}
          pressId={press}
          labelHint={open && peeking && !tapped ? (
            // The demonstration's first row (2026-09-08 拍板): the tag says what
            // it is the result of, once, in the tree's own light. A row of the
            // tag — it hangs, fades and goes out with the title and the doors —
            // and only until this browser has tapped a light of its own.
            <div className="banyan-label-hint"><span>Tap any light.</span></div>
          ) : null}
          labelActions={open ? (
            // The demonstration hangs exactly the tag a tap hangs (2026-09-07
            // 二次拍板): it exists to show what touching a light gives you, and
            // a tag with its doors taken off showed something else. Only the
            // fact recorded differs — the tree hung this one by itself
            // (tree_tag_demo), so "tags opened" stays what a person did; the
            // key carries that apart so the row is re-observed if the person
            // then taps the light the demonstration was already on.
            <div className="banyan-label-actions" data-log-seen={peeking ? 'tree_tag_demo' : 'tree_light_open'} data-log-state={!peeking && linked ? 'arrival' : undefined} data-log-node-id={open.id} key={`${peeking ? 'demo' : linked ? 'arrival' : 'open'}-${open.id}`}>
              <button
                type="button"
                className="banyan-label-cta"
                data-log="tree_light_tell"
                data-log-node-id={open.id}
                data-log-discovery="tree"
                onClick={() => tell(open)}
              >
                <LabelFlame />
                <span>I’ve been here too</span>
              </button>
              <Link to={`/places/${encodeURIComponent(open.slug)}`} className="banyan-label-more" data-log="tree_light_open" data-log-node-id={open.id} data-log-state="place">
                What did they go through?
              </Link>
            </div>
          ) : null}
          letters={sceneLetters}
          onLettersTap={() => { endPeek(); setLettersOpen(true); }}
          wide={wide}
          onLightTap={openHere}
          onBackgroundTap={close}
          // Ember keeps wandering through the tree's own demonstration (a tag
          // it hung by itself); it pauses for a tag the person opened, the
          // letters, and its own card.
          emberActive={(!open || peeking) && !lettersOpen && !emberCard && onScreen && !composing}
          emberHeld={emberCard !== null}
          onEmberTap={(anchor) => { endPeek(); setOpen(null); setEmberCard(anchor); }}
          signpost={sessionStatus === 'authenticated' ? (
            <ChatsSign
              lit={chatsUnread(chats) > 0}
              friends={chats?.friends?.eligible === true}
              onHelp={signQuiet ? undefined : () => setIntro('sign')}
              fresh={!signQuiet && chats?.friends?.fresh === true}
              reveal={landing}
            />
          ) : null}
          clock
        />
        {emberCard ? <EmberTapCard anchor={emberCard} /> : null}
        {intro ? <MeetPeopleIntro from={intro} onClose={introClosed} foldTo={() => hostRef.current?.querySelector('.friends-sign') ?? null} /> : null}
      </div>
      {/* The same lights as a list (2026-09-28 拍板). The scene is SVG that
          says nothing to a screen reader or a crawler, so the window's lights
          stand here too, as links — the tree's textual equivalent, kept off
          the screen (TreeHome.css). Until this list a light's page had no link
          from any page, only a sitemap row, and Google left most of them
          unfetched; the page's own crawlable path to a light is this. */}
      {nodes.length > 0 ? (
        <nav className="tree-home-lights-index" aria-label="Lights on the tree today">
          <ul>
            {nodes.map((n) => (
              <li key={n.id}>
                <Link to={`/places/${encodeURIComponent(n.slug)}`} data-log="tree_light_open" data-log-node-id={n.id} data-log-state="index">{n.title}</Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
      {lettersOpen ? (
        <KnockLetter
          knocks={knocks}
          onChange={updateKnock}
          initialExpanded={initialExpanded}
          onClose={() => { setLettersOpen(false); setInitialExpanded(''); }}
          onOpened={() => letterTick((n) => n + 1)}
        />
      ) : null}
      <div className={`tree-home-nav${navDocked ? ' is-docked' : ''}`} ref={navRef} data-eh-top-bar>
        {/* The search lists the lights the words could mean; choosing one is
            the same walk as tapping it in the canopy. */}
        {nav(<TreeSearch onPick={walkTo} onTell={() => openTalk({ entryType: 'home', returnTo: '/' })} />, yourEmber, hasApp)}
      </div>
      <div className="tree-home-ground banyan-ground">
        {/* The ground's door (2026-09-16 拍板): the headline, one line — AI,
            what it draws on, who decides — then the composer itself: the
            first sentence typed here is the first turn, its two doors work with or
            without one, and the empty field asks (placeholder; the 09-15 rotating
            light row read as a display and people stopped tapping the field).
            It stays whatever happens in the canopy: a light's own doors hang on
            its tag, up at the light. No privacy line under it (2026-10-27
            founder: 0.4% of visitors opened it; the consent at the first send
            and the footer carry privacy).
            A browser that has already talked here (2026-09-16 拍板, §0.8 A):
            the sentence written for a stranger gives its slot to the tail of
            its own conversation, drawn by the composer — Ember's last line,
            itself a door back; the field and its two doors are unchanged. */}
        <div className="tree-home-door">
          {home ? (
            <>
              <h1 id="landing-title" className="tree-home-say">{home.hero.headline}</h1>
              {thread ? null : <p className="tree-home-sub">{home.hero.supporting_text}</p>}
            </>
          ) : (
            <div className="tree-home-say-loading" aria-live="polite">
              {failed ? <p>Ember is briefly unreachable. Refresh to try again.</p> : null}
            </div>
          )}
          <HeroComposer
            placeholder={home?.hero.composer_placeholder ?? ''}
            returnPlaceholder={home?.hero.composer_placeholder_return ?? ''}
            tail={tail}
            lights={home?.tree?.nodes ?? []}
            dock
          />
        </div>
      </div>
    </div>
  );
}
